"use client";

import { useEffect, useState } from "react";
import { Select, SelectItem } from "@/components/ui/select";
import { BOARD_FACULTY_CODE, catalogApi, type Faculty } from "@/lib/catalog-api.service";

/**
 * Nhãn của `Board` trong mọi picker gán khoa.
 *
 * `Board` không phải một khoa — server đọc nó là "không giới hạn", tức tài khoản
 * giữ mã này xem được dữ liệu của mọi khoa. Nhãn lấy cố định ở đây chứ không lấy
 * `faculty.name` trong DB, để một lần ai đó đổi tên hàng đó thì mục quan trọng
 * nhất của dropdown không đổi nghĩa theo.
 */
const BOARD_LABEL = "Ban giám hiệu";

/**
 * Danh sách khoa đang hoạt động, tải một lần cho mỗi component dùng nó.
 *
 * `failed` được tách khỏi `loading` vì hai trạng thái này dẫn tới hai hành xử
 * khác nhau ở phía gọi: đang tải thì chờ, hỏng thì phải khoá control lại —
 * không được để dropdown rỗng, vì rỗng đọc thành "không thuộc khoa nào".
 */
export function useFaculties() {
	const [faculties, setFaculties] = useState<Faculty[]>([]);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		let cancelled = false;

		catalogApi
			.listFaculties()
			.then((list) => {
				if (!cancelled) setFaculties(list);
			})
			.catch((err: unknown) => {
				console.error("Failed to load faculties:", err);
				if (!cancelled) setFailed(true);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, []);

	return { faculties, loading, failed };
}

/**
 * Tên hiển thị của một mã khoa. Dùng chung cho dropdown và cho chỗ chỉ đọc
 * (thẻ user trong danh sách quản lý).
 */
export function facultyLabel(code: string, faculties: Faculty[]): string {
	if (code === BOARD_FACULTY_CODE) return BOARD_LABEL;
	return faculties.find((f) => f.code === code)?.name ?? code;
}

interface FacultySelectProps {
	/** `""` nghĩa là không gán khoa nào. */
	value: string;
	onChange: (code: string) => void;
	disabled?: boolean;
	id?: string;
}

/**
 * Chọn khoa để gán cho một tài khoản.
 *
 * Khác `statistics-tabs/FacultyFilter`: đây là ghi quyền, không phải thu hẹp
 * phạm vi xem. Nên nó có `Board` (bộ lọc thì không — lọc theo "mọi khoa" là vô
 * nghĩa) và không bao giờ tự ghim theo khoa của người đang đăng nhập.
 */
export function FacultySelect({ value, onChange, disabled, id }: FacultySelectProps) {
	const { faculties, loading, failed } = useFaculties();

	// Khoa đã ngừng hoạt động không còn trong catalog, nhưng tài khoản được gán
	// trước đó vẫn giữ quyền xem — ngừng hoạt động chỉ gỡ khoa khỏi picker, không
	// thu hồi. Nếu không thêm lại ở đây, mở modal rồi bấm Lưu sẽ âm thầm xoá mất
	// khoa đang có.
	const isRetired = !!value && !loading && !failed && !faculties.some((f) => f.code === value);

	const options = [
		...faculties.map((f) => ({ code: f.code, label: facultyLabel(f.code, faculties) })),
		...(isRetired ? [{ code: value, label: `${value} (đã ngừng hoạt động)` }] : []),
	];

	return (
		<>
			<Select
				id={id}
				value={value}
				disabled={disabled || loading || failed}
				onChange={(e) => onChange(e.target.value)}
			>
				<SelectItem value="">— Không gán khoa —</SelectItem>
				{options.map((option) => (
					<SelectItem key={option.code} value={option.code}>
						{option.label}
					</SelectItem>
				))}
			</Select>
			{failed && (
				<p className="text-xs text-red-600 dark:text-red-400 mt-1">
					Không tải được danh sách khoa. Vui lòng tải lại trang.
				</p>
			)}
			{isRetired && (
				<p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
					Khoa hiện tại đã ngừng hoạt động. Giữ nguyên nếu không muốn thu hồi quyền xem.
				</p>
			)}
			{value === BOARD_FACULTY_CODE && (
				<p className="text-xs text-muted-foreground mt-1">Xem được dữ liệu của tất cả các khoa.</p>
			)}
		</>
	);
}
